import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { RefreshCw, Calendar, ChevronDown, ChevronUp, Printer, Eye, Edit, Bell, Video, PlayCircle, Sparkles, HelpCircle, Hospital, Plus } from 'lucide-react';
import api from '../api';
import { RecertDueCard } from '../components/RecertDueCard';
import { MyCaseloadBoard } from '../components/MyCaseloadBoard';
import { toast } from 'sonner';

const MainDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expandedWidgets, setExpandedWidgets] = useState({
    census: true,
    visits: true,
    pocUnsent: true,
    ordersUnsent: true,
    patientsInHospital: true,
    pocDue: true,
    incompleteDocs: true,
    recentOasis: true,
    credentialsDue: true,
    coSignature: true,
    noticeOfElection: true
  });
  
  // Visits filter state
  const [visitPeriod, setVisitPeriod] = useState('Today');
  const [visitFromDate, setVisitFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [visitToDate, setVisitToDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  
  // Patients in Hospital state
  const [patientsInHospital, setPatientsInHospital] = useState([]);
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [hospitalForm, setHospitalForm] = useState({
    patientId: '',
    patientName: '',
    admissionDate: new Date().toISOString().split('T')[0],
    hospitalName: '',
    reason: '',
    notes: ''
  });
  
  // Real data states
  const [censusData, setCensusData] = useState([]);
  const [visitsData, setVisitsData] = useState([]);
  const [patientsData, setPatientsData] = useState([]);
  const [documentationData, setDocumentationData] = useState([]);
  const [oasisData, setOasisData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [alertsData, setAlertsData] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Check for token in ivory_user object (consistent with LoginPage)
      const userData = localStorage.getItem('ivory_user');
      if (!userData) {
        navigate('/login');
        return;
      }
      const parsedUser = JSON.parse(userData);
      if (!parsedUser.token) {
        navigate('/login');
        return;
      }

      const [patientsRes, appointmentsRes, docsRes, oasisRes, staffRes, alertsRes] = await Promise.all([
        api.get('/patients'),
        api.get('/appointments'),
        api.get('/documentation'),
        api.get('/forms/oasis'),
        api.get('/staff').catch(() => ({ data: { success: true, staff: [] } })),
        api.get('/alerts')
      ]);

      // Process patients for census
      if (patientsRes.data.success) {
        const patients = patientsRes.data.patients;
        setPatientsData(patients);
        
        // Calculate census by insurance
        const insuranceCounts = {};
        patients.forEach(p => {
          const insurance = p.insurance || 'PRIVATE PAY';
          insuranceCounts[insurance] = (insuranceCounts[insurance] || 0) + 1;
        });
        
        const colors = ['#2ecc71', '#3498db', '#9b59b6', '#e74c3c', '#f39c12', '#1abc9c'];
        const total = patients.length || 1;
        const census = Object.entries(insuranceCounts).map(([payer, count], i) => ({
          payer,
          count,
          percentage: Math.round((count / total) * 100),
          color: colors[i % colors.length]
        }));
        setCensusData(census);
      }

      // Process appointments for visits
      if (appointmentsRes.data.success) {
        const appointments = appointmentsRes.data.appointments;
        const visits = appointments.map(apt => ({
          patient: apt.patientName,
          pan: '1',
          date: ((d) => (/^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : d))(String(apt.appointmentDate || apt.date || '')),
          time: apt.time || 'TBD',
          phone: apt.phone || 'N/A',
          address: apt.address || 'N/A',
          id: apt.id
        }));
        setVisitsData(visits);
      }

      // Process documentation
      if (docsRes.data.success) {
        const docs = docsRes.data.documentation.filter(d => d.status !== 'Completed');
        setDocumentationData(docs);
      }

      // Process OASIS assessments
      if (oasisRes.data.success) {
        const oasis = oasisRes.data.assessments.slice(0, 10).map(o => ({
          id: o.id,
          asmtCompDt: o.assessmentDate,
          patientName: o.patientName,
          reason: o.assessmentType,
          status: o.status,
          hipps: 'N/A'
        }));
        setOasisData(oasis);
      }

      // Process staff
      if (staffRes.data.success) {
        setStaffData(staffRes.data.staff || []);
      }

      // Process alerts
      if (alertsRes.data.success) {
        setAlertsData(alertsRes.data.alerts || []);
      }

      // Fetch patients in hospital
      try {
        const hospitalRes = await api.get('/patients/in-hospital');
        if (hospitalRes.data.success) {
          setPatientsInHospital(hospitalRes.data.patients || []);
        }
      } catch (e) {
        // Endpoint may not exist yet, use empty array
        setPatientsInHospital([]);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleWidget = (widget) => {
    setExpandedWidgets(prev => ({ ...prev, [widget]: !prev[widget] }));
  };

  // Filter visits by date range
  const applyVisitFilter = () => {
    const from = new Date(visitFromDate);
    const to = new Date(visitToDate);
    to.setHours(23, 59, 59); // Include the entire end date
    
    const filtered = visitsData.filter(visit => {
      const visitDate = new Date(visit.date);
      return visitDate >= from && visitDate <= to;
    });
    setFilteredVisits(filtered);
    toast.success(`Showing ${filtered.length} visits from ${visitFromDate} to ${visitToDate}`);
  };

  // Handle period change for visits filter
  const handlePeriodChange = (period) => {
    setVisitPeriod(period);
    const today = new Date();
    let from = new Date();
    let to = new Date();

    switch(period) {
      case 'Today':
        from = today;
        to = today;
        break;
      case 'This Week':
        from = new Date(today.setDate(today.getDate() - today.getDay()));
        to = new Date();
        break;
      case 'This Month':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date();
        break;
      case 'Last 7 Days':
        from = new Date(Date.now() - 7*24*60*60*1000);
        to = new Date();
        break;
      default:
        from = today;
        to = today;
    }
    setVisitFromDate(from.toISOString().split('T')[0]);
    setVisitToDate(to.toISOString().split('T')[0]);
  };

  // Add patient to hospital
  const handleAddToHospital = async () => {
    if (!hospitalForm.patientName || !hospitalForm.hospitalName) {
      toast.error('Please fill in patient name and hospital name');
      return;
    }
    try {
      const res = await api.post('/patients/hospitalize', hospitalForm);
      if (res.data.success) {
        toast.success('Patient hospitalization recorded');
        setPatientsInHospital(prev => [...prev, res.data.hospitalization]);
        setShowHospitalModal(false);
        setHospitalForm({
          patientId: '',
          patientName: '',
          admissionDate: new Date().toISOString().split('T')[0],
          hospitalName: '',
          reason: '',
          notes: ''
        });
      }
    } catch (error) {
      toast.error('Failed to record hospitalization');
    }
  };

  // Initialize filtered visits when visitsData loads
  useEffect(() => {
    setFilteredVisits(visitsData);
  }, [visitsData]);

  const WidgetHeader = ({ title, widgetKey, showPrint = false, count = null }) => (
    <div 
      className="flex items-center justify-between p-3 bg-gray-50 border-b cursor-pointer hover:bg-gray-100"
      onClick={() => toggleWidget(widgetKey)}
    >
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        {count !== null && (
          <span className="px-2 py-0.5 bg-[#f39c12] text-white text-xs rounded-full">{count}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {showPrint && <Printer className="w-4 h-4 text-gray-500" />}
        {expandedWidgets[widgetKey] ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </div>
    </div>
  );

  const DataTable = ({ columns, data, emptyMessage = "No data available", onRowClick }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col, i) => (
              <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-3 py-4 text-center text-gray-500">{emptyMessage}</td></tr>
          ) : (
            data.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => onRowClick && onRowClick(row)}>
                {Object.values(row).filter((_, idx) => idx < columns.length).map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-gray-700">{cell}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
        <span>Showing {data.length} entries</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#3498db]" />
        </div>
      </DashboardLayout>
    );
  }

  // Prepare data for tables
  const incompleteDocsTable = documentationData.map(d => ({
    action: (
      <div className="flex gap-1">
        <button onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/documentation`); }} className="p-1 hover:bg-blue-100 rounded">
          <Eye className="w-4 h-4 text-blue-500" />
        </button>
      </div>
    ),
    visitDate: new Date(d.dueDate).toLocaleDateString(),
    patient: d.patientName,
    status: d.status,
    docType: d.type,
    id: d.id
  }));

  const recentOasisTable = oasisData.map(o => ({
    action: (
      <div className="flex gap-1">
        <button onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/forms/oasis?id=${o.id}`); }} className="p-1 hover:bg-blue-100 rounded">
          <Eye className="w-4 h-4 text-blue-500" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/forms/soc-roc?id=${o.id}`); }} className="p-1 hover:bg-green-100 rounded">
          <Edit className="w-4 h-4 text-green-500" />
        </button>
      </div>
    ),
    asmtCompDt: o.asmtCompDt,
    patientName: o.patientName,
    reason: o.reason,
    status: o.status,
    hipps: o.hipps,
    id: o.id
  }));

  const credentialsAlerts = alertsData.filter(a => a.type === 'credential_expiry');
  const credentialsTable = credentialsAlerts.map(a => ({
    caregiver: a.message?.split(' credential')[0] || 'Staff Member',
    credential: a.message?.includes('License') ? 'License' : 'CPR/BLS',
    expiration: a.dueDate ? new Date(a.dueDate).toLocaleDateString() : 'Soon',
    remarks: a.priority
  }));

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="main-dashboard">
        <MyCaseloadBoard />
        {/* COVID-19 Alert Banner */}
        <div className="bg-[#f39c12] text-white px-4 py-2 rounded flex items-center justify-between">
          <span>Welcome to Ivory Home Health Care Dashboard</span>
          <button onClick={fetchAllData} className="px-4 py-1 bg-[#27ae60] text-white rounded text-sm hover:bg-[#2ecc71] flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh Data
          </button>
        </div>

        <RecertDueCard />

        {/* Quick Links - Alora Plus Style */}
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: 'Announcements', icon: Bell, color: '#3b82f6', border: '#93c5fd' },
            { label: 'Live Training', icon: PlayCircle, color: '#f59e0b', border: '#fcd34d' },
            { label: 'Training Videos', icon: Video, color: '#8b5cf6', border: '#c4b5fd' },
            { label: 'Add Features', icon: Sparkles, color: '#22c55e', border: '#86efac' },
            { label: 'FAQ', icon: HelpCircle, color: '#ef4444', border: '#fca5a5' }
          ].map((item, i) => (
            <button 
              key={i} 
              onClick={() => toast.info(`${item.label} - Coming Soon`)}
              className="flex items-center justify-center gap-2 p-4 bg-white rounded-lg border-2 hover:shadow-md transition-all"
              style={{ borderColor: item.border }}
              data-testid={`quick-link-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" style={{ color: item.color }} />
              <span className="text-sm text-gray-700 font-medium">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Functional Quick Links */}
        <div className="grid grid-cols-6 gap-3">
          {[
            { label: 'New Patient', path: '/dashboard/patients' },
            { label: 'New OASIS', path: '/dashboard/clinical/oasis' },
            { label: 'Nurses Notes', path: '/dashboard/skilled-nursing/nurses-notes' },
            { label: 'Plan of Care/485', path: '/dashboard/clinical/poc-485' },
            { label: 'Documentation', path: '/dashboard/documentation' },
            { label: 'Alerts', path: '/dashboard/alerts' }
          ].map((item, i) => (
            <button 
              key={i} 
              onClick={() => navigate(item.path)}
              className="flex items-center justify-center gap-2 p-3 bg-white rounded border hover:shadow-md hover:border-[#f39c12] transition-all text-center"
              data-testid={`functional-link-${item.label.toLowerCase().replace(/ /g, '-').replace('/', '-')}`}
            >
              <span className="text-sm text-gray-700">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Current Census By Payer */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Current Census By Payer" widgetKey="census" showPrint count={patientsData.length} />
              {expandedWidgets.census && (
                <div className="p-4">
                  {censusData.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No patients in census</p>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="relative w-48 h-48">
                        <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                          {censusData.reduce((acc, item, i) => {
                            const prevOffset = acc.offset;
                            const dashArray = item.percentage;
                            acc.circles.push(
                              <circle
                                key={i}
                                cx="50" cy="50" r="40"
                                fill="transparent"
                                stroke={item.color}
                                strokeWidth="20"
                                strokeDasharray={`${dashArray} ${100 - dashArray}`}
                                strokeDashoffset={-prevOffset}
                              />
                            );
                            acc.offset = prevOffset + dashArray;
                            return acc;
                          }, { circles: [], offset: 0 }).circles}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-700">{patientsData.length}</span>
                        </div>
                      </div>
                      <div className="space-y-2 ml-4">
                        {censusData.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: item.color }}></div>
                            <span>{item.payer}: {item.count} ({item.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* POC/485 Unsent */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="POC/485 Unsent" widgetKey="pocUnsent" />
              {expandedWidgets.pocUnsent && (
                <div className="p-4">
                  <DataTable 
                    columns={['Action', 'Patient', 'CertFrom', 'CertTo', 'PAN', 'Type']}
                    data={patientsData.slice(0, 5).map(p => ({
                      action: (
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/clinical/poc-485?patientId=${p.id}`); }} className="p-1 hover:bg-blue-100 rounded">
                          <Eye className="w-4 h-4 text-blue-500" />
                        </button>
                      ),
                      patient: `${p.firstName} ${p.lastName}`,
                      certFrom: new Date().toLocaleDateString(),
                      certTo: new Date(Date.now() + 60*24*60*60*1000).toLocaleDateString(),
                      pan: '1',
                      type: 'POC'
                    }))}
                    onRowClick={(row) => navigate('/dashboard/clinical/poc-485')}
                  />
                </div>
              )}
            </div>

            {/* Patients In Hospital */}
            <div className="bg-white rounded-lg shadow border">
              <div 
                className="flex items-center justify-between p-3 bg-gray-50 border-b cursor-pointer hover:bg-gray-100"
                onClick={() => toggleWidget('patientsInHospital')}
              >
                <div className="flex items-center gap-2">
                  <Hospital className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold text-gray-800">Patients In Hospital</h3>
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{patientsInHospital.length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowHospitalModal(true); }}
                    className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    data-testid="add-hospital-patient-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  {expandedWidgets.patientsInHospital ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </div>
              </div>
              {expandedWidgets.patientsInHospital && (
                <div className="p-4">
                  <DataTable 
                    columns={['Patient', 'Hospital', 'Admission Date', 'Reason', 'Status']}
                    data={patientsInHospital.map(h => ({
                      patient: h.patientName,
                      hospital: h.hospitalName,
                      admissionDate: new Date(h.admissionDate).toLocaleDateString(),
                      reason: h.reason || 'N/A',
                      status: h.status || 'Admitted'
                    }))}
                    emptyMessage="No patients currently in hospital"
                  />
                </div>
              )}
            </div>

            {/* Orders Unsent */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Orders Unsent" widgetKey="ordersUnsent" />
              {expandedWidgets.ordersUnsent && (
                <div className="p-4">
                  <DataTable 
                    columns={['Action', 'Patient', 'Order Date', 'PAN', 'Signed']}
                    data={[]}
                    emptyMessage="No unsigned orders"
                  />
                </div>
              )}
            </div>

            {/* Incomplete Documents */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Incomplete Documents" widgetKey="incompleteDocs" count={documentationData.length} />
              {expandedWidgets.incompleteDocs && (
                <div className="p-4">
                  <DataTable 
                    columns={['Action', 'Visit Date', 'Patient', 'Status', 'Doc Type']}
                    data={incompleteDocsTable}
                    onRowClick={(row) => navigate('/dashboard/documentation')}
                  />
                </div>
              )}
            </div>

            {/* Credentials Due */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Credentials Due" widgetKey="credentialsDue" count={credentialsTable.length} />
              {expandedWidgets.credentialsDue && (
                <div className="p-4">
                  <DataTable 
                    columns={['Caregiver', 'Credential', 'Expiration', 'Priority']}
                    data={credentialsTable}
                    emptyMessage="No credentials expiring soon"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Visits with Filter */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Visits" widgetKey="visits" showPrint count={filteredVisits.length} />
              {expandedWidgets.visits && (
                <div className="p-4">
                  {/* Visits Filter Controls - Alora Plus Style */}
                  <div className="flex items-end gap-4 mb-4 pb-4 border-b">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Period</label>
                      <select 
                        value={visitPeriod}
                        onChange={(e) => handlePeriodChange(e.target.value)}
                        className="px-3 py-2 border rounded-md text-sm bg-white min-w-[120px]"
                        data-testid="visit-period-select"
                      >
                        <option value="Today">Today</option>
                        <option value="This Week">This Week</option>
                        <option value="This Month">This Month</option>
                        <option value="Last 7 Days">Last 7 Days</option>
                        <option value="Custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <div className="relative">
                        <input 
                          type="date" 
                          value={visitFromDate}
                          onChange={(e) => setVisitFromDate(e.target.value)}
                          className="px-3 py-2 border rounded-md text-sm bg-gray-50"
                          data-testid="visit-from-date"
                        />
                      </div>
                    </div>
                    <div className="text-gray-400">-</div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <div className="relative">
                        <input 
                          type="date" 
                          value={visitToDate}
                          onChange={(e) => setVisitToDate(e.target.value)}
                          className="px-3 py-2 border rounded-md text-sm bg-gray-50"
                          data-testid="visit-to-date"
                        />
                      </div>
                    </div>
                    <button 
                      onClick={applyVisitFilter}
                      className="px-4 py-2 bg-[#3b82f6] text-white rounded-md text-sm hover:bg-[#2563eb] transition-colors"
                      data-testid="visit-apply-filter"
                    >
                      Apply
                    </button>
                  </div>
                  <DataTable 
                    columns={['Patient', 'PAN', 'Date', 'Time', 'Home Phone', 'Address']}
                    data={filteredVisits.slice(0, 10)}
                    onRowClick={() => navigate('/dashboard/scheduling')}
                  />
                </div>
              )}
            </div>

            {/* POC/485's Due */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="POC/485's Due" widgetKey="pocDue" />
              {expandedWidgets.pocDue && (
                <div className="p-4">
                  <DataTable 
                    columns={['Case Manager', 'Patient', 'PAN', 'CertDue', '5 Day Window']}
                    data={patientsData.slice(0, 3).map(p => ({
                      caseManager: 'Admin',
                      patient: `${p.firstName} ${p.lastName}`,
                      pan: '1',
                      certDue: new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString(),
                      window: '5 days'
                    }))}
                  />
                </div>
              )}
            </div>

            {/* Recent OASIS */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Recent OASIS" widgetKey="recentOasis" count={oasisData.length} />
              {expandedWidgets.recentOasis && (
                <div className="p-4">
                  <DataTable 
                    columns={['Action', 'Date', 'Patient', 'Type', 'Status']}
                    data={recentOasisTable}
                    onRowClick={(row) => navigate(`/dashboard/forms/oasis?id=${row.id}`)}
                  />
                </div>
              )}
            </div>

            {/* Co-Signature Needed */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Co-Signature Needed" widgetKey="coSignature" />
              {expandedWidgets.coSignature && (
                <div className="p-4">
                  <DataTable 
                    columns={['Action', 'From', 'To', 'Doc', 'Patient']}
                    data={[]}
                    emptyMessage="No co-signatures needed"
                  />
                </div>
              )}
            </div>

            {/* Notice of Election */}
            <div className="bg-white rounded-lg shadow border">
              <WidgetHeader title="Notice of Election - Unsent" widgetKey="noticeOfElection" />
              {expandedWidgets.noticeOfElection && (
                <div className="p-4">
                  <DataTable 
                    columns={['Patient', 'Admit Date', 'Sent Date', '# days']}
                    data={[]}
                    emptyMessage="No pending notices"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 py-4">
          © 2026 Ivory Healthcare Systems, LLC. Version 8.61.4.19
        </div>

        {/* Hospital Admission Modal */}
        {showHospitalModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="hospital-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Hospital className="w-6 h-6 text-red-500" />
                Document Patient Hospitalization
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name *</label>
                  <select 
                    value={hospitalForm.patientId}
                    onChange={(e) => {
                      const patient = patientsData.find(p => p.id === e.target.value);
                      setHospitalForm(prev => ({
                        ...prev,
                        patientId: e.target.value,
                        patientName: patient ? `${patient.firstName} ${patient.lastName}` : ''
                      }));
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="hospital-patient-select"
                  >
                    <option value="">Select Patient</option>
                    {patientsData.map(p => (
                      <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hospital Name *</label>
                  <input 
                    type="text"
                    value={hospitalForm.hospitalName}
                    onChange={(e) => setHospitalForm(prev => ({ ...prev, hospitalName: e.target.value }))}
                    placeholder="Enter hospital name"
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="hospital-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admission Date</label>
                  <input 
                    type="date"
                    value={hospitalForm.admissionDate}
                    onChange={(e) => setHospitalForm(prev => ({ ...prev, admissionDate: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="hospital-admission-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Hospitalization</label>
                  <input 
                    type="text"
                    value={hospitalForm.reason}
                    onChange={(e) => setHospitalForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="e.g., Surgery, Observation, Emergency"
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="hospital-reason-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea 
                    value={hospitalForm.notes}
                    onChange={(e) => setHospitalForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="hospital-notes-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button 
                  onClick={() => setShowHospitalModal(false)}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                  data-testid="hospital-cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddToHospital}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  data-testid="hospital-save-btn"
                >
                  Save Hospitalization
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MainDashboard;
